import { useEffect, useMemo, useRef, useState } from 'react';
import GLMap, { GeolocateControl, type MapRef, Source, Layer } from 'react-map-gl-v7';
import type { FeatureCollection } from 'geojson';
import debounce from 'lodash/debounce';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useIsDesktop } from '@firsttable/web-theme';
import usePrevious from '@discover-lib/usePrevious';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import { type Restaurant } from '@discover-data/gql/getters/mapRestaurants';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import getFilterDates from '@discover-data/stores/restaurant/model/date/getFilterDates';
import getQuerySessions from '@discover-data/stores/restaurant/model/session/getQuerySessions';
import useDate from '@discover-data/stores/hooks/date/useDate';
import useDiscountOnly from '@discover-data/stores/hooks/discountOnly/useDiscountOnly';
import useFavourite from '@discover-data/stores/hooks/favourite/useFavourite';
import useHasLastTable from '@discover-data/stores/hooks/hasLastTable/useHasLastTable';
import useRegularOnly from '@discover-data/stores/hooks/regularOnly/useRegularOnly';
import useSession from '@discover-data/stores/hooks/session/useSession';
import useSuburbs from '@discover-data/stores/hooks/suburb/useSuburbs';
import useTagIds from '@discover-data/stores/hooks/tagIds/useTagIds';
import Dots from '@discover-ui/dots';
import { useAuthState } from '@/components/context/AuthContext';
import { NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN } from '@/lib/env';
import {
  useLoadImages,
  icons,
  clusterLayer,
  clusterCountLayer,
  restaurantPinsLayer,
  restaurantNameLayer,
  selectedRestaurantPinLayer,
  restaurantImagesLayer,
  selectedRestaurantImagesLayer,
  restaurantBadgeLayer,
  selectedRestaurantBadgeLayer,
  restaurantRatingBadgeLayer,
  restaurantNewBadgeLayer,
  selectedRestaurantRatingBadgeLayer,
  selectedRestaurantNewBadgeLayer,
  restaurantStarLayer,
  selectedRestaurantStarLayer,
  setupClusters,
  setupRestaurantLayers,
} from './pins';
import MapCard from './card';
import onChange from './onChange';
import RestaurantMapEmptyMessage from './emptyMessage';
import { getSessionViewState, setSessionViewState } from './sessionViewState';
import {
  getSessionSelectedRestaurantId,
  setSessionSelectedRestaurantId,
  removeSessionSelectedRestaurantId,
} from './sessionSelectedRestaurantId';
import getChildAreaBounds from './childAreaBounds';
import DEFAULT_ZOOM_LEVEL from './defaultZoomLevel';

const CHILD_AREA_BOUNDS_PADDING = 48;

export type MapProps = {
  defaultViewState: ViewState;
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

export default function RestaurantMap({
  defaultViewState,
  tags,
  suburbs,
  peopleOptions,
  minDiners,
  maxDiners,
}: MapProps) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const prevMapLoaded = usePrevious(mapLoaded);

  // filters
  const [date] = useDate();
  const prevDate = usePrevious(date);
  const [session] = useSession();
  const prevSession = usePrevious(session);
  const [discountOnly] = useDiscountOnly();
  const prevDiscountOnly = usePrevious(discountOnly);
  const [regularOnly] = useRegularOnly();
  const prevRegularOnly = usePrevious(regularOnly);
  const [hasLastTable] = useHasLastTable();
  const prevHasLastTable = usePrevious(hasLastTable);
  const [tagIds] = useTagIds(tags);
  const prevTagIds = usePrevious(tagIds);
  const [selectedSuburbs] = useSuburbs(suburbs);
  const suburb = selectedSuburbs[0] || null;
  const discoverEnabled = useStoreState((state) => state.discoverEnabled);
  const prevDiscoverEnabled = usePrevious(discoverEnabled);
  const discountDatesToShow = useStoreState((state) => state.discountDatesToShow);
  const prevDiscountDatesToShow = usePrevious(discountDatesToShow);
  const { user } = useAuthState();
  const [favourite] = useFavourite(user?.favouriteIds);
  const prevFavourite = usePrevious(favourite);

  // view state
  const sessionViewState = getSessionViewState();
  const childAreaBounds = sessionViewState ? null : getChildAreaBounds(suburb, suburbs);
  const initialViewState = sessionViewState || {
    latitude: defaultViewState.latitude,
    longitude: defaultViewState.longitude,
    zoom: defaultViewState.zoom || DEFAULT_ZOOM_LEVEL,
  };
  const [viewState, setViewState] = useState(initialViewState);
  const prevViewState = usePrevious(viewState);
  const debouncedSetViewState = debounce((newViewState: ViewState) => {
    setSessionViewState(newViewState);
    setViewState(newViewState);
  }, 350);

  // restaurants
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState<Map<Restaurant['id'], Restaurant>>(new Map());

  const restaurantsSignature = Array.from(restaurants.keys()).join(); // used to track changes for useEffect hooks

  // selected restaurant
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(
    getSessionSelectedRestaurantId() || null
  );

  // save selected restaurant in session storage
  useEffect(() => {
    if (selectedRestaurantId === null) {
      removeSessionSelectedRestaurantId();
    } else {
      setSessionSelectedRestaurantId(selectedRestaurantId);
    }
  }, [selectedRestaurantId]);

  // reset selected restaurant if it's no longer available
  useEffect(() => {
    if (loading) {
      return;
    }

    const selectedRestaurantAvailable = Array.from(restaurants.keys()).some(
      (id) => id === selectedRestaurantId
    );

    if (!selectedRestaurantAvailable) {
      setSelectedRestaurantId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantsSignature, loading, selectedRestaurantId]);

  // handle changes to the map view state or the filters
  const mapRef = useRef<MapRef | null>(null);
  const tagIdsSignature = tagIds.join();
  const discountDatesToShowSignature = discountDatesToShow.join();
  const favouriteSignature = JSON.stringify(favourite);
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) {
      return;
    }

    const mapLoadedChanged = mapLoaded !== prevMapLoaded;

    const dateChanged = date !== prevDate;
    const sessionChanged = session !== prevSession;
    const discountOnlyChanged = discountOnly !== prevDiscountOnly;
    const regularOnlyChanged = regularOnly !== prevRegularOnly;
    const hasLastTableChanged = hasLastTable !== prevHasLastTable;
    const tagIdsChanged = tagIds.join() !== prevTagIds.join();
    const discoverEnabledChanged = discoverEnabled !== prevDiscoverEnabled;
    const discountDatesToShowChanged =
      discountDatesToShow.join() !== prevDiscountDatesToShow.join();
    const favouriteChanged = favourite !== prevFavourite;

    const filtersChanged =
      dateChanged ||
      sessionChanged ||
      discountOnlyChanged ||
      regularOnlyChanged ||
      hasLastTableChanged ||
      tagIdsChanged ||
      discoverEnabledChanged ||
      discountDatesToShowChanged ||
      favouriteChanged;

    const viewStateChanged = JSON.stringify(viewState) !== JSON.stringify(prevViewState);

    if (!mapLoadedChanged && !filtersChanged && !viewStateChanged) {
      return;
    }

    onChange({
      centerPoint: viewState,
      queryVariables: {
        status: ['LIVE'],
        availability: {
          forSession: getQuerySessions([session], hasLastTable),
          onDate: getFilterDates({
            date,
            regularOnly,
            discountDatesToShow,
          }),
        },
        tags: tagIds,
        offersFirstTable: discountOnly || undefined,
        demo: false,
      },
      mapRef: mapRef.current,
      setLoading,
      setRestaurants: (_restaurants) => {
        // keep previous results if the filters didn't change
        const newRestaurants = filtersChanged
          ? new Map<Restaurant['id'], Restaurant>()
          : restaurants;

        for (const restaurant of _restaurants) {
          // Only add the restaurant if it's not filtered by favorites
          if (!favourite || favourite.includes(restaurant.id)) {
            newRestaurants.set(restaurant.id, restaurant);
          }
        }

        setRestaurants(newRestaurants);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapLoaded,
    date,
    session,
    discountOnly,
    regularOnly,
    hasLastTable,
    tagIdsSignature,
    discoverEnabled,
    discountDatesToShowSignature,
    viewState,
    favouriteSignature,
  ]);

  // restaurant features
  const restaurantFeatures: FeatureCollection = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: Array.from(restaurants.values()).map(
        ({ id, lng, lat, offersFirstTable, rating, menuTitle, heroImage }) => {
          const isSelected = id === selectedRestaurantId;
          const basePinImage = 'pin-blue-base';
          const selectedBasePinImage = 'pin-red-base';

          return {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [parseFloat(lng), parseFloat(lat)] },
            properties: {
              id,
              isSelected,
              iconImage: basePinImage,
              selectedIconImage: selectedBasePinImage,
              offersFirstTable,
              rating: rating || 0,
              title: menuTitle,
              hasImage: !!heroImage,
            },
          };
        }
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restaurantsSignature, selectedRestaurantId]
  );

  useLoadImages(mapRef, mapLoaded, restaurants);

  const isDesktop = useIsDesktop();

  return (
    <>
      <GLMap
        id="map"
        ref={mapRef}
        mapboxAccessToken={NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
        initialViewState={
          childAreaBounds
            ? {
                ...initialViewState,
                bounds: childAreaBounds,
                fitBoundsOptions: { padding: CHILD_AREA_BOUNDS_PADDING },
              }
            : viewState
        }
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v10"
        minZoom={4}
        onMove={(viewStateChangeEvent) => {
          const { latitude, longitude, zoom } = viewStateChangeEvent.viewState;

          debouncedSetViewState({ latitude, longitude, zoom });
        }}
        onLoad={() => {
          const currentMapRef = mapRef.current;

          if (!currentMapRef) {
            return;
          }

          // load icon assets
          for (const { name, width, height, content } of icons) {
            const resolutionFactor = 2;
            const customIcon = new Image(width * resolutionFactor, height * resolutionFactor);
            customIcon.onload = () =>
              currentMapRef.addImage(name, customIcon, { pixelRatio: resolutionFactor });
            customIcon.src = `data:image/svg+xml,${content}`;
          }

          // Setup restaurant layer interactions
          setupRestaurantLayers(currentMapRef, setSelectedRestaurantId);

          // Setup cluster interactions
          setupClusters(currentMapRef);

          if (!sessionViewState && childAreaBounds) {
            const center = currentMapRef.getCenter();
            // The restaurant query reads this state. onMove only writes it after a debounce.
            setViewState({
              latitude: center.lat,
              longitude: center.lng,
              zoom: currentMapRef.getZoom(),
            });
          } else if (!sessionViewState && suburb?.latitude != null && suburb?.longitude != null) {
            currentMapRef.flyTo({
              center: [suburb.longitude, suburb.latitude],
              zoom: suburb.zoom || DEFAULT_ZOOM_LEVEL,
            });
          }

          // load initial restaurants
          setMapLoaded(true);
        }}
        doubleClickZoom={false}>
        {loading && (
          <Dots className="absolute left-1/2 top-5 z-40 -translate-x-1/2 bg-white p-4 shadow" />
        )}
        <MapCard
          className="absolute inset-x-0 bottom-0 z-40 sm:p-2"
          id={selectedRestaurantId}
          onClose={() => {
            setSelectedRestaurantId(null);
          }}
          peopleOptions={peopleOptions}
        />
        <Source
          id="restaurants"
          type="geojson"
          data={restaurantFeatures}
          cluster
          clusterMaxZoom={17}
          clusterRadius={50}
          clusterProperties={{
            sum: ['+', ['get', 'point_count']],
          }}>
          {/* Clusters first */}
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />

          {/* Regular restaurant pins and components */}
          <Layer {...restaurantPinsLayer} />
          <Layer {...restaurantImagesLayer} />
          <Layer {...restaurantBadgeLayer} />
          <Layer {...restaurantRatingBadgeLayer} />
          <Layer {...restaurantNewBadgeLayer} />
          <Layer {...restaurantStarLayer} />

          {/* Selected restaurant pins and components - these MUST be last to appear on top */}
          <Layer {...selectedRestaurantPinLayer} />
          <Layer {...selectedRestaurantImagesLayer} />
          <Layer {...selectedRestaurantBadgeLayer} />
          <Layer {...selectedRestaurantRatingBadgeLayer} />
          <Layer {...selectedRestaurantNewBadgeLayer} />
          <Layer {...selectedRestaurantStarLayer} />

          {/* Restaurant names at the very top */}
          <Layer {...restaurantNameLayer} />
        </Source>
        <GeolocateControl trackUserLocation={!isDesktop} showUserHeading={!isDesktop} />
      </GLMap>
      {restaurants.size === 0 && !loading && (
        <RestaurantMapEmptyMessage
          tags={tags}
          peopleOptions={peopleOptions}
          minDiners={minDiners}
          maxDiners={maxDiners}
          className="absolute bottom-0 z-20 w-full rounded-t-md bg-white shadow sm:bottom-2 sm:size-fit sm:rounded-md sm:shadow-2xl sm:shadow-black"
        />
      )}
    </>
  );
}
